import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// 二次确认弹窗：仅用于不可逆或重要操作（接受 / 拒绝 / 取消 / 过期 / 转合同）。
// 普通草稿保存不走此组件。可选采集「原因」并回传（对应后端 reason 字段）。
// 注意：使用 createPortal 挂到 document.body，避免被表格 sticky 单元格（td.op-col）
// 的层叠上下文遮挡。
export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message?: React.ReactNode;
  detail?: React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  tone?: 'primary' | 'danger';
  requireReason?: boolean;
  reasonPlaceholder?: string;
  busy?: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open, title, message, detail, confirmLabel, cancelLabel = '取消',
  tone = 'primary', requireReason, reasonPlaceholder, busy, onConfirm, onCancel,
}: ConfirmDialogProps) {
  const [reason, setReason] = useState('');
  const firstRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (open) {
      setReason('');
      // 打开时聚焦主操作或原因输入
      setTimeout(() => firstRef.current?.focus(), 20);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;
  const canSubmit = !requireReason || reason.trim().length > 0;

  return createPortal(
    <div className="overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal-head">{title}</div>
        <div className="modal-body">
          {message ? <div className="modal-msg">{message}</div> : null}
          {detail ? <div className="modal-detail">{detail}</div> : null}
          {requireReason ? (
            <label className="field" style={{ marginTop: 12 }}>
              <label>原因（必填，写入审计留痕）</label>
              <textarea
                className="txt area"
                rows={2}
                value={reason}
                placeholder={reasonPlaceholder || '请简要说明原因…'}
                onChange={(e) => setReason(e.target.value)}
              />
            </label>
          ) : null}
        </div>
        <div className="modal-foot">
          <div className="spacer" />
          <button className="btn" onClick={onCancel} disabled={busy}>{cancelLabel}</button>
          <button
            ref={firstRef as React.RefObject<HTMLButtonElement>}
            className={`btn ${tone === 'danger' ? 'danger' : 'primary'}`}
            disabled={busy || !canSubmit}
            onClick={() => onConfirm(reason.trim())}
          >
            {busy ? '处理中…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
